// In-memory state for the /demo experience. No persistence — refresh resets.
//
// Mirrors the shapes that real dashboard pages expect (Campaign, Lead, Site,
// userData) so the cloned UI stays as faithful as possible to the live app.

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import type { Site } from '@shared/types';
import { setDemoAnalyticsBlocked } from '@/lib/firebase';
import { generateMockLeads, DemoCampaign, DemoLead, NICHE_SERVICES } from './mockLeadsData';
import { generateMockSiteHtml } from './generateMockSite';
import { buildDemoSeed } from './demoSeedData';

export interface DemoSite extends Omit<Site, 'createdAt' | 'updatedAt' | 'soldAt'> {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  soldAt?: Date;
  generatedHtml: string;
}

const DEFAULT_USER_DATA = {
  fullName: 'Demo User',
  companyName: 'Romulus Demo',
  planId: 'cashflow' as const,
  email: 'demo@romulus.app',
  subscriptionActive: true,
  onboardingCompleted: true,
  stripeConnectStatus: 'active' as const,
  stripeConnectAccountId: 'acct_demo',
  quotas: { sitesLimit: 100, leadsLimit: 1000 },
  usage: { sitesUsed: 0, leadsUsed: 0 },
};

interface DemoState {
  userData: typeof DEFAULT_USER_DATA;
  campaigns: DemoCampaign[];
  leads: DemoLead[];
  sites: DemoSite[];
  addCampaign: (input: Omit<DemoCampaign, 'id' | 'createdAt'>) => DemoCampaign;
  deleteCampaign: (id: string) => void;
  scrapeCampaign: (campaignId: string) => Promise<{ leadsCreated: number; nicheSearched: string }>;
  generateSite: (
    leadId: string,
    templateType: string,
    selectedFeatures: string[],
    selectedLayouts: string[],
  ) => Promise<{ siteId: string }>;
  markSiteSent: (siteId: string) => void;
  markSiteSold: (siteId: string) => void;
  updateSite: (siteId: string, updates: Partial<DemoSite>) => void;
  getSite: (siteId: string) => DemoSite | undefined;
  getLeadByCampaignId: (campaignId: string) => DemoLead[];
  getLead: (leadId: string) => DemoLead | undefined;
}

const DemoStateContext = createContext<DemoState | null>(null);

export function useDemoState(): DemoState {
  const ctx = useContext(DemoStateContext);
  if (!ctx) throw new Error('useDemoState must be used inside DemoStateProvider');
  return ctx;
}

export function DemoStateProvider({ children }: { children: ReactNode }) {
  // Build the curated seed once per provider mount. useRef so the seed
  // computation never re-runs on re-renders and React's strict-mode double
  // invoke won't produce mismatched seeds.
  const seedRef = useRef<ReturnType<typeof buildDemoSeed> | null>(null);
  if (!seedRef.current) seedRef.current = buildDemoSeed();
  const seed = seedRef.current;

  const [campaigns, setCampaigns] = useState<DemoCampaign[]>(seed.campaigns);
  const [leads, setLeads] = useState<DemoLead[]>(seed.leads);
  const [sites, setSites] = useState<DemoSite[]>(seed.sites);

  // Block GA4 collection while inside the demo. Direct visits to /demo never
  // init analytics (gated in firebase.ts), but for SPA navigation from / into
  // /demo this disables the live SDK so realtime active-user pings stop.
  useEffect(() => {
    setDemoAnalyticsBlocked(true);
    return () => { setDemoAnalyticsBlocked(false); };
  }, []);

  const usage = {
    leadsUsed: leads.length,
    sitesUsed: sites.length,
  };
  const userData = { ...DEFAULT_USER_DATA, usage };

  const addCampaign = useCallback((input: Omit<DemoCampaign, 'id' | 'createdAt'>): DemoCampaign => {
    const id = `demo-camp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const campaign: DemoCampaign = {
      ...input,
      id,
      userId: 'demo-user',
      createdAt: new Date(),
    };
    setCampaigns((prev) => [campaign, ...prev]);
    return campaign;
  }, []);

  const deleteCampaign = useCallback((id: string) => {
    setCampaigns((prev) => prev.filter((c) => c.id !== id));
    setLeads((prev) => prev.filter((l) => l.campaignId !== id));
  }, []);

  const scrapeCampaign = useCallback(async (campaignId: string) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (!campaign) throw new Error('Campaign not found');

    const newLeads = generateMockLeads(campaign);
    let appendedCount = 0;
    setLeads((prev) => {
      // Dedupe by id — re-scraping a seeded or previously-scraped campaign
      // should top up missing leads, not duplicate ones already on screen.
      const existing = new Set(prev.map((l) => l.id));
      const toAppend = newLeads.filter((l) => !existing.has(l.id));
      appendedCount = toAppend.length;
      // Prepend so the just-scraped leads sit at the TOP of the Leads tab and
      // the dashboard's Recent Leads — otherwise a fresh scrape (e.g.
      // Jacksonville) hides below the older seeded leads (e.g. Austin).
      return [...toAppend, ...prev];
    });
    setCampaigns((prev) =>
      prev.map((c) => (c.id === campaignId ? { ...c, lastScrapedAt: new Date() } : c)),
    );

    return {
      leadsCreated: appendedCount,
      nicheSearched: campaign.nicheLabel || campaign.niche,
    };
  }, [campaigns]);

  const generateSite = useCallback(
    async (leadId: string, templateType: string, selectedFeatures: string[], selectedLayouts: string[]) => {
      const lead = leads.find((l) => l.id === leadId);
      if (!lead) throw new Error('Lead not found');

      const serviceNames = NICHE_SERVICES[lead.niche] || ['Quality service', 'Professional team', 'Local expertise', 'Fair pricing'];
      const html = generateMockSiteHtml({ lead, templateType, selectedFeatures, services: serviceNames });
      const niceNiche = lead.niche.replace(/_/g, ' ');

      const siteId = `demo-site-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const newSite: DemoSite = {
        id: siteId,
        userId: 'demo-user',
        leadId,
        name: lead.businessName,
        templateType: templateType as DemoSite['templateType'],
        status: 'draft',
        views: 0,
        generatedHtml: html,
        // Structured content lets the editor's text/services/testimonial
        // panels populate immediately from the lead's mock data.
        content: {
          headline: `${lead.businessName} — trusted ${niceNiche} in ${lead.location}`,
          aboutText: `${lead.businessName} has been serving ${lead.location} and surrounding areas with reliable ${niceNiche} service. Honest pricing, real expertise, and work that lasts.`,
          services: serviceNames.map((name) => ({
            name,
            description: `Professional ${name.toLowerCase()} done right the first time.`,
          })),
          testimonials: lead.googleReviews.slice(0, 3).map((r) => ({
            name: r.authorName,
            content: r.text,
            rating: r.rating,
          })),
          seo: {
            title: `${lead.businessName} | ${niceNiche} in ${lead.location}`,
            description: `Professional ${niceNiche} services in ${lead.location}.`,
          },
          faq: [],
          videoUrl: '',
        },
        siteMeta: {
          templateId: templateType,
          sitePersonality: 'professional',
          primaryAngle: `Trusted local ${niceNiche} in ${lead.location}`,
          sectionsUsed: ['hero', 'services', 'gallery', 'reviews', 'contact'],
        },
        businessData: {
          phone: lead.phone,
          rating: lead.rating,
          reviewCount: lead.reviewCount ?? 0,
          businessHours: lead.businessHours,
          photos: lead.businessImages,
          googleMapsUrl: lead.googleMapsUrl,
          googleReviews: lead.googleReviews,
        },
        selectedFeatures,
        selectedLayouts,
        previewToken: `demo-token-${siteId}`,
        price: 29900,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      setSites((prev) => [newSite, ...prev]);
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, siteGenerated: true, status: 'contacted' } : l)),
      );

      return { siteId };
    },
    [leads],
  );

  const markSiteSent = useCallback((siteId: string) => {
    setSites((prev) =>
      prev.map((s) => (s.id === siteId ? { ...s, status: 'sent', updatedAt: new Date() } : s)),
    );
  }, []);

  const markSiteSold = useCallback((siteId: string) => {
    setSites((prev) =>
      prev.map((s) =>
        s.id === siteId ? { ...s, status: 'sold', soldAt: new Date(), updatedAt: new Date() } : s,
      ),
    );
  }, []);

  const updateSite = useCallback((siteId: string, updates: Partial<DemoSite>) => {
    setSites((prev) =>
      prev.map((s) => (s.id === siteId ? { ...s, ...updates, updatedAt: new Date() } : s)),
    );
  }, []);

  const getSite = useCallback((siteId: string) => sites.find((s) => s.id === siteId), [sites]);
  const getLead = useCallback((leadId: string) => leads.find((l) => l.id === leadId), [leads]);
  const getLeadByCampaignId = useCallback(
    (campaignId: string) => leads.filter((l) => l.campaignId === campaignId),
    [leads],
  );

  const value: DemoState = {
    userData,
    campaigns,
    leads,
    sites,
    addCampaign,
    deleteCampaign,
    scrapeCampaign,
    generateSite,
    markSiteSent,
    markSiteSold,
    updateSite,
    getSite,
    getLead,
    getLeadByCampaignId,
  };

  return <DemoStateContext.Provider value={value}>{children}</DemoStateContext.Provider>;
}
